import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { ThemeContextProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { indiceDaRota } from "./components/NavegacaoEtapa/etapas";
import { chaveDaTransicao, ehTelaDeAlturaCheia } from "./utils/transicaoDeRota";
import { DadosProvider } from "./context/DadosContext";
import { ExportRenderProvider } from "./context/ExportRenderContext";
import { PrecosProvider } from "./context/PrecosContext";
import { PreferenciasTabelasProvider } from "./context/PreferenciasTabelasContext";
import { ToastProvider } from "./context/ToastContext";
import { AgendamentoProvider } from "./context/AgendamentoContext";
import GlobalStyles from "./styles/GlobalStyles";
import { PageState } from "./styles/shared";
import { PageLoader } from "./components/LoadingMarcenaria";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import MfaEnrollmentBanner from "./components/MfaEnrollmentBanner";
import MfaStepUpModal from "./components/MfaStepUpModal";
import PageTransition from "./components/PageTransition";
import ToastStack from "./components/Toast";
import ErrorBoundary from "./components/ErrorBoundary";
import { useSidebarPreference } from "./hooks/useSidebarPreference";
import { DEFAULT_PATH } from "./utils/rotas";
import {
  MODULO_CENTRAL_PROJETOS,
  MODULO_PLANILHA_LEGADO,
  MODULO_PROJETOS_EXECUTIVOS,
} from "./config/modulos";
import { ehAmbienteLocal, ehHostDoPainel } from "./utils/hostDoPainel";
import { BP_TABLET } from "./styles/canonical/breakpoints";
import AcessosKeyboardShortcuts from "./components/AcessosKeyboardShortcuts";
import { WhatsappNaoLidasProvider } from "./hooks/useWhatsappNaoLidas";
import { NotificacoesProvider } from "./hooks/useNotificacoesAgregadas";
import { whatsappHabilitado } from "./utils/whatsappHabilitado";
// Imports estaticos das paginas: TODAS entram no bundle e ficam disponiveis de
// imediato — navegacao entre telas instantanea (sem baixar chunk por pagina).
import Login from "./pages/Auth/Login";
import MfaVerify from "./pages/Auth/MfaVerify";
import TelaDeEntrada from "./pages/Auth/entrada/TelaDeEntrada";
import PrimeiroAcesso from "./pages/Auth/PrimeiroAcesso";
import ConviteUsuario from "./pages/ConviteUsuario";
import Configuracoes from "./pages/Configuracoes";
import Clientes from "./pages/Clientes";
import Home from "./pages/Home";
import VisaoGeral from "./pages/VisaoGeral";
import DevDesignSystem from "./pages/DevDesignSystem";
import ClientesLegado from "./pages/Clientes/Legado";
import RevisorFerragens from "./pages/RevisorFerragens";
import LiberacaoAmbientes from "./pages/LiberacaoAmbientes";
import FilaMedicao from "./pages/FilaMedicao";
import ElaboracaoExecutivo from "./pages/ElaboracaoExecutivo";
import AprovacaoExecutivo from "./pages/AprovacaoExecutivo";
import Revisao from "./pages/Revisao";
import LiberacaoPPCP from "./pages/LiberacaoPPCP";
import PlanejamentoEntregas from "./pages/PlanejamentoEntregas";
import RomaneiosLista from "./pages/Romaneios";
import RomaneioEditor from "./pages/Romaneios/Editor";
import DefinicaoAcabamentos from "./pages/DefinicaoAcabamentos";
import Compras from "./pages/Compras";
import SeparacaoFerragens from "./pages/SeparacaoFerragens";
import Industrializacao from "./pages/Industrializacao";
import Marcenaria from "./pages/Marcenaria";
import Terceiros from "./pages/Terceiros";
import Acabamento from "./pages/Acabamento";
import Marceneiros from "./pages/Marceneiros";
import Obras from "./pages/Obras";
import KpisPPCP from "./pages/KpisPPCP";
import KpisTempos from "./pages/KpisTempos";
import VisaoFinanceira from "./pages/VisaoFinanceira";
import MapaProducao from "./pages/MapaProducao";
import Relatorios from "./pages/Relatorios";
import CapacidadeFabrica from "./pages/CapacidadeFabrica";
import AreaPcp from "./pages/AreaPcp";
import Apontamentos from "./pages/Apontamentos";
import AvisoDeVersaoNova from "./components/AvisoDeVersaoNova";
import PaginaSetorMapa from "./pages/MapaProducao/setor";
import ProjetosExecutivos from "./pages/ProjetosExecutivos";
import CentralProjetos from "./pages/CentralProjetos";
import AutorizarClaude from "./pages/CentralProjetos/AutorizarClaude";
import RetornoGoogle from "./pages/Configuracoes/RetornoGoogle";
import { PE_ROUTE_ANY } from "./pages/ProjetosExecutivos/pePerms";
import Orcamentos from "./pages/Orcamentos";
import Crm from "./pages/Crm";
import Pedidos from "./pages/Pedidos";
import ConfiguracoesComercial from "./pages/Comercial/Configuracoes";
import PainelComercial from "./pages/Comercial/Painel";
import Parceiros from "./pages/Comercial/Parceiros";
import ParceiroDetalhe from "./pages/Comercial/Parceiros/Detalhe";
import TemplatesPage from "./pages/Comercial/Templates";
import Formularios from "./pages/Formularios";
import FormularioPublico from "./pages/FormularioPublico";
import OrcamentoPublico from "./pages/OrcamentoPublico";
import ContratoPublico from "./pages/ContratoPublico";
import Acompanhamento from "./pages/Acompanhamento";
import Kanbans from "./pages/Kanbans";
import Suprimentos from "./pages/Suprimentos";
import SolicitacoesInternas from "./pages/Suprimentos/Solicitacoes";
import GestaoSolicitacoes from "./pages/Suprimentos/GestaoSolicitacoes";
import PainelAssistencias from "./pages/Suprimentos/PainelAssistencias";
import RequisicoesCompra from "./pages/Suprimentos/RequisicoesCompra";
import RequisicaoDetalhePagina from "./pages/Suprimentos/RequisicoesCompra/DetalhePagina";
import RequisicoesSeparacao from "./pages/Suprimentos/RequisicoesSeparacao";
import TerminalShell from "./pages/Terminal";
import ProducaoV2Shell from "./pages/ProducaoV2";
import TerceirizacaoShell from "./pages/Terceirizacao";
import LogisticaShell from "./pages/Logistica";
import EntregaPublica from "./pages/EntregaPublica";
import PecaPublica from "./pages/PecaPublica";
import SatisfacaoPublica from "./pages/SatisfacaoPublica";
import PosvendaShell from "./pages/Posvenda";
import FinanceiroShell from "./pages/Financeiro";
import FiscalShell from "./pages/Fiscal";
import CobrancaShell from "./pages/Cobranca";
import EmailsShell from "./pages/Emails";
import Webhooks from "./pages/Webhooks";
import Whatsapp from "./pages/Whatsapp";
import RotaDaAgenda from "./pages/Agenda/Rota";
import AvisosDaAgenda from "./components/AvisosDaAgenda";
import TourDoErp from "./components/Tour/TourDoErp";
import AvisoDaPharu from "./components/AvisoDaPharu";
import ChatPage from "./pages/Chat";
import { ChatNaLateral, useChatNaLateralVisivel } from "./pages/Chat/ChatNaLateral";
import { GuardiaoDasNotificacoes } from "./components/NotificacoesDoChat";
import SincronizadorPreferencias from "./components/SincronizadorPreferencias";
import {
  PERMS_HUB_SOLICITACOES,
  PERMS_ROTA_GESTAO_SOLICITACOES,
} from "./services/solicitacoes";

// Painel da Pharu e proposta pública: carregados sob demanda, para o CSS e o JS
// do painel não entrarem no pacote que todo usuário do ERP baixa.
const PainelPharu = lazy(() => import("./pages/Painel"));
const PropostaPharuPublica = lazy(() => import("./pages/PropostaPharuPublica"));
const CartaoCadastrado = lazy(() => import("./pages/CartaoCadastrado"));

// Larguras canônicas do shell (Bloco D). Mantidas como constantes JS porque
// o padding-left da MainArea é dinâmico (collapsed → 64, expanded → 248).
const SIDEBAR_WIDTH_EXPANDED = 248;
const SIDEBAR_WIDTH_COLLAPSED = 64;
// Drawer threshold — abaixo de 1024px a sidebar fixa some (drawer assume).
const SIDEBAR_BREAKPOINT_PX = BP_TABLET;

const MainArea = styled.main`
  min-height: 100vh;
  /* --mfa-banner-h: altura da faixa de MFA (0 quando ausente), para o conteudo
     descer e nao ficar atras da faixa fixed de largura total (P2). */
  padding-top: calc(var(--topbar-height) + var(--safe-area-top, 0px) + var(--mfa-banner-h, 0px));
  padding-left: ${({ $sidebarWidth }) => $sidebarWidth}px;
  transition: padding-left 0.18s ease;

  /* Chat na lateral (2026-11-03): na tela larga a página abre espaço para a
     coluna da direita (sem cobrir nada); abaixo disso a coluna flutua por cima. */
  @media (min-width: 1280px) {
    ${({ $chatNaLateral }) => ($chatNaLateral ? "margin-right: var(--chat-lateral-largura); max-width: none;" : "")}
  }
  background: var(--bg);

  /* Board (/crm) ocupa 100% da largura: anula o max-width/centro/padding
     globais de main (system.css) apenas nessa rota. */
  ${({ $fullBleed }) =>
    $fullBleed
      ? "max-width: none; margin-left: 0; margin-right: 0; padding-right: 0; padding-bottom: 0;"
      : ""}

  /* Re-skin GP (mockup): glow radial sutil no topo-direito, na PÁGINA inteira
     (não em cada card), somente nas 15 telas legadas do fluxo de produção. */
  ${({ $flow }) =>
    $flow
      ? `background-image: radial-gradient(1100px 560px at 82% -60px, var(--accent-soft), transparent 68%); background-repeat: no-repeat;`
      : ""}

  @media (max-width: ${SIDEBAR_BREAKPOINT_PX}px) {
    /* Sem sidebar fixa (modo drawer), main não deve ter gutter horizontal — o
       PageContainer provê os 16px simétricos. Zerar só o padding-left deixava o
       padding-right global (system.css: padding 32px nas laterais) intacto,
       criando margem direita > esquerda no mobile. */
    padding-left: 0;
    padding-right: 0;
  }
`;

const PageContent = styled.div`
  width: 100%;
  min-height: calc(100vh - var(--topbar-height) - var(--safe-area-top, 0px));
`;

/**
 * Componente de protecao de rota.
 *
 * Props (em ordem de avaliacao):
 * - `modulo`: modulo contratado (ADR-0102, `config/modulos.js`). Sem ele a
 *   rota se comporta como caminho inexistente: vai para o destino padrao,
 *   igual ao catch-all — nem Master entra, porque e contrato, nao permissao.
 * - `masterOnly`: restringe a usuarios com cargo `is_master_role=True`.
 *   Atalho legitimo para endpoints muito sensiveis sem codigo `recurso:acao`
 *   dedicado.
 * - `requires`: array de codigos `recurso:acao` — TODOS sao obrigatorios.
 * - `requireAny`: array de codigos `recurso:acao` — pelo menos UM basta.
 *
 * Sprint B da migracao RBAC: parametro `pageId` foi removido. Identificadores
 * legados (`kpis-ppcp`, `liberacao`, etc.) viraram codigos `<recurso>:view`
 * via backfill (cargos/migrations/0004) e o catalogo do banco ja contem
 * todos os recursos correspondentes.
 */
function ProtectedRoute({
  modulo,
  masterOnly = false,
  requires,
  requireAny,
  children,
}) {
  const location = useLocation();
  const {
    authenticated,
    hasAllPermissions,
    hasAnyPermission,
    isMaster,
    loading,
    permissoes,
    primeiroAcesso,
    temModulo,
  } = useAuth();

  if (loading) {
    return <PageLoader scope="auth" />;
  }

  if (!authenticated) {
    // Caminho + query: a autorização do Claude (OAuth) volta com os parâmetros.
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  if (primeiroAcesso) {
    return <Navigate to="/primeiro-acesso" replace />;
  }

  // Só decide com as permissões carregadas: antes delas, redirecionar
  // perderia o link de quem abriu a tela direto.
  if (modulo && permissoes && !temModulo(modulo)) {
    return <Navigate to={DEFAULT_PATH} replace />;
  }

  if (masterOnly && !isMaster) {
    return <PageState $tone="error">Acesso restrito a usuários Master.</PageState>;
  }

  if (Array.isArray(requires) && requires.length > 0 && !hasAllPermissions(requires)) {
    return <PageState $tone="error">Você não tem permissão para acessar esta página.</PageState>;
  }

  if (Array.isArray(requireAny) && requireAny.length > 0 && !hasAnyPermission(requireAny)) {
    return <PageState $tone="error">Você não tem permissão para acessar esta página.</PageState>;
  }

  return children;
}

function ProtectedPage({ modulo, masterOnly = false, requires, requireAny, children }) {
  return (
    <ProtectedRoute
      modulo={modulo}
      masterOnly={masterOnly}
      requires={requires}
      requireAny={requireAny}
    >
      <PageTransition>{children}</PageTransition>
    </ProtectedRoute>
  );
}

// Fase D — Acessos foi movido para /configuracoes/acessos. Redireciona as URLs
// antigas (e itens de sidebar) preservando a sub-rota (cargos, auditoria, etc).
function AcessosRedirect() {
  const location = useLocation();
  const resto = location.pathname.replace(/^\/acessos/, "");
  return <Navigate to={`/configuracoes/acessos${resto}${location.search}`} replace />;
}

// O Revisor de Ferragens saiu do Legado para Suprimentos (ADR-0102): o link
// antigo (favorito, histórico do navegador) continua chegando, com a sub-rota.
function RevisorFerragensRedirect() {
  const location = useLocation();
  const resto = location.pathname.replace(/^\/legado\/revisor-ferragens/, "");
  return <Navigate to={`/suprimentos/revisor-ferragens${resto}${location.search}`} replace />;
}

function AuthenticatedShell() {
  const location = useLocation();
  const { collapsed, toggle: toggleCollapsed, forcedRail } = useSidebarPreference();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const chatNaLateral = useChatNaLateralVisivel();

  return (
    <ProtectedRoute>
      <DadosProvider>
        <PreferenciasTabelasProvider>
        <PrecosProvider>
          <ExportRenderProvider>
          <AgendamentoProvider>
          <WhatsappNaoLidasProvider>
          <NotificacoesProvider>
          <Header onOpenSidebar={() => setMobileSidebarOpen(true)} />
          <Sidebar
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            canToggle={!forcedRail}
            mobileOpen={mobileSidebarOpen}
            onCloseMobile={() => setMobileSidebarOpen(false)}
          />
          <MainArea
            $sidebarWidth={sidebarWidth}
            $chatNaLateral={chatNaLateral}
            $fullBleed={
              location.pathname === "/crm" ||
              // Gestão de Solicitações: a vista kanban ocupa a altura toda
              // da viewport (scroll só dentro das lanes) — o padding-bottom
              // global do main criava ~96px de scroll morto na página.
              // O PageContainer interno preserva max-width/padding visuais.
              location.pathname.startsWith("/suprimentos/gestao-solicitacoes") ||
              location.pathname.startsWith("/comercial/templates") ||
              // REF-12 / REF-13: telas legadas Pintura & Lacagem e Terceiros
              // ocupam toda a largura disponivel (anula o teto de 1440px +
              // centralizacao do <main> global). Igualdade exata para nao
              // pegar "/acabamentos" (tela de Definicao de Acabamentos).
              location.pathname === "/acabamento" ||
              location.pathname === "/terceiros" ||
              // Pagina do setor do Mapa da Producao: largura total da tela
              // (kanban por subsetor, pedido do dono em 2026-09-22).
              location.pathname.startsWith("/legado/mapa/setor/") ||
              // Modo execucao da Area do PCP: tres colunas no desktop e o
              // botao Feito fixo no rodape do celular.
              location.pathname.startsWith("/legado/area-pcp/execucoes/") ||
              // Chat e conversas/Assistente da Central de Projetos: ocupam
              // toda a área livre, sem teto de largura nem rolagem da página
              // (pedido do Igor, 2026-10-28).
              ehTelaDeAlturaCheia(location.pathname) ||
              // Re-skin GP: todas as 15 telas legadas do fluxo usam full-bleed
              // (o PainelTabela centraliza a si mesmo em 1560px).
              indiceDaRota(location.pathname) >= 0
            }
            $flow={indiceDaRota(location.pathname) >= 0}
            data-app-shell="true"
          >
            {/* Sprint 10 — banner persistente quando MFA e exigido pelo cargo
                mas o usuario ainda nao enrollou. Auto-some apos enrollment. */}
            <MfaEnrollmentBanner />
            <AvisoDaPharu />
            {/* Modal global de step-up MFA: abre quando uma operacao
                protegida retorna 403 "Esta operacao exige verificacao MFA
                recente.". Permite revalidar TOTP sem perder contexto. */}
            <MfaStepUpModal />
            {/* Atalhos globais: g u/c/a/s/h navegacao, n novo, / busca, ? ajuda. */}
            <AcessosKeyboardShortcuts />
            <AvisosDaAgenda />
            {/* Tour do primeiro acesso e "Rever o tour" (ADR-0104). */}
            <TourDoErp />
            <PageContent>
            <AnimatePresence mode="wait">
              <Routes location={location} key={chaveDaTransicao(location.pathname)}>
              <Route path="/" element={<Navigate to={DEFAULT_PATH} replace />} />
              <Route path="/home" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["dashboard:view"]}><Home /></ProtectedPage>} />
              <Route path="/visao-geral" element={<ProtectedPage><VisaoGeral /></ProtectedPage>} />
              {/* /dev/design-system — vitrine interna dos primitivos Bloco D
                  (Sprint 3). B27: o comentário anterior dizia "é só QA
                  visual" e deixava a rota aberta a qualquer autenticado.
                  Tela de desenvolvimento não é para o marceneiro que
                  digitou a URL errada — e a vitrine expõe componentes e
                  nomenclatura interna. Master, como as demais telas de
                  bastidor. */}
              <Route path="/dev/design-system" element={<ProtectedPage masterOnly><DevDesignSystem /></ProtectedPage>} />
              {/* Fase D — Perfil movido para o hub de Configurações. */}
              <Route path="/perfil" element={<Navigate to="/configuracoes/perfil" replace />} />
              {/* Hub unificado de Configurações (A3): router-shell com sub-rotas
                  (/configuracoes/perfil, /seguranca, /notificacoes, /aparencia,
                  /planilhas, /exportacao, /importacoes, /integracoes,
                  /integracoes-cad, /acessos/*). RBAC por aba. As rotas antigas
                  dos módulos movidos continuam ativas até a fase D (redirects). */}
              <Route path="/configuracoes/*" element={<ProtectedPage><Configuracoes /></ProtectedPage>} />
              {/* /acessos/* — router-shell com sub-rotas (Hub, Usuarios, Cargos, Auditoria, SoD).
                  Master-only por design (Sprint C remove a sub-rota /acessos/legado). */}
              <Route path="/acessos/*" element={<AcessosRedirect />} />
              {/* Plano de Suprimentos (2026-09): as telas legadas de Sobras / Cores MDF /
                  Fitas de borda foram substituidas pelo Cadastro de Suprimentos e pelo
                  Aproveitamento de Sobras. URLs antigas redirecionam (D04/D19). */}
              <Route path="/cadastro-sobras" element={<Navigate to="/suprimentos/sobras" replace />} />
              <Route path="/gestao-sobras" element={<Navigate to="/suprimentos/sobras" replace />} />
              <Route path="/gestao-cores-mdf" element={<Navigate to="/suprimentos/produtos" replace />} />
              <Route path="/gestao-fitas-borda" element={<Navigate to="/suprimentos/produtos" replace />} />
              {/* /clientes/* — router-shell ERP novo (Fase 3).
                  Sub-rotas: index=Lista backend, :id=ficha ERP.
                  HF-D4: visao legada (planilha + drill-down Mercado Livre)
                  vive isolada em /legado/clientes/*. */}
              <Route path="/clientes/*" element={<ProtectedPage requires={["comercial:view"]}><Clientes /></ProtectedPage>} />
              {/* HF-D4: shell legado isolado — Planilha + drill-down (pedidos
                  accordion + ambiente timeline). */}
              <Route path="/legado/clientes/*" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["comercial:view"]}><ClientesLegado /></ProtectedPage>} />
              {/* Revisor de Lista de Ferragens (legado — proxy DinaBox + historico). */}
              <Route path="/legado/revisor-ferragens/*" element={<RevisorFerragensRedirect />} />
              {/* HF-D4 retrocompat: URLs antigas /clientes/planilha*
                  redirecionam para a nova rota legada. */}
              <Route path="/clientes/planilha" element={<Navigate to="/legado/clientes" replace />} />
              <Route path="/clientes/planilha/*" element={<Navigate to="/legado/clientes" replace />} />
              <Route path="/liberacao" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["liberacao:view"]}><LiberacaoAmbientes /></ProtectedPage>} />
              <Route path="/acabamentos" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["acabamentos:view"]}><DefinicaoAcabamentos /></ProtectedPage>} />
              <Route path="/medicao" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["medicao:view"]}><FilaMedicao /></ProtectedPage>} />
              <Route path="/executivo" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["executivo:view"]}><ElaboracaoExecutivo /></ProtectedPage>} />
              <Route path="/aprovacao" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["aprovacao:view"]}><AprovacaoExecutivo /></ProtectedPage>} />
              <Route path="/revisao" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["revisao:view"]}><Revisao /></ProtectedPage>} />
              <Route path="/ppcp" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["ppcp:view"]}><LiberacaoPPCP /></ProtectedPage>} />
              <Route path="/compras" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["compras:view"]}><Compras /></ProtectedPage>} />
              <Route path="/ferragens" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["ferragens:view"]}><SeparacaoFerragens /></ProtectedPage>} />
              <Route path="/industrializacao" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["industrializacao:view"]}><Industrializacao /></ProtectedPage>} />
              <Route path="/marcenaria" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["marcenaria:view"]}><Marcenaria /></ProtectedPage>} />
              <Route path="/terceiros" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["terceiros:view"]}><Terceiros /></ProtectedPage>} />
              <Route path="/acabamento" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["acabamento:view"]}><Acabamento /></ProtectedPage>} />
              <Route path="/marceneiros" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["marceneiros:view"]}><Marceneiros /></ProtectedPage>} />
              <Route path="/marceneiros/:marceneiroId" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["marceneiros:view"]}><Marceneiros /></ProtectedPage>} />
              <Route path="/entregas" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["entregas:view"]}><PlanejamentoEntregas /></ProtectedPage>} />
              <Route path="/romaneios" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["romaneios:view"]}><RomaneiosLista /></ProtectedPage>} />
              <Route path="/romaneios/novo" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["romaneios:view", "romaneios:edit"]}><RomaneioEditor /></ProtectedPage>} />
              <Route path="/romaneios/:id" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["romaneios:view"]}><RomaneioEditor /></ProtectedPage>} />
              <Route path="/obras" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["obras:view"]}><Obras /></ProtectedPage>} />
              <Route path="/kpis-ppcp" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["kpis_ppcp:view"]}><KpisPPCP /></ProtectedPage>} />
              <Route path="/kpis-tempos" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["kpis_tempos:view"]}><KpisTempos /></ProtectedPage>} />
              <Route path="/financeiro" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["financeiro:view"]}><VisaoFinanceira /></ProtectedPage>} />
              {/* Mapa da Producao substitui o Planejamento Marcenaria (planilha),
                  que nunca entrou em uso (plano do Mapa, secao 5.2). A rota antiga
                  redireciona; os arquivos da pagina antiga ficam ate a remocao. */}
              <Route path="/legado/relatorios" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["relatorios_legado:view"]}><Relatorios /></ProtectedPage>} />
              <Route path="/legado/capacidade" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["capacidade_fabrica:view"]}><CapacidadeFabrica /></ProtectedPage>} />
              {/* Area do PCP (ADR-0087): manuais editaveis + execucao em checklist. */}
              <Route path="/legado/area-pcp/*" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["area_pcp:view"]}><AreaPcp /></ProtectedPage>} />
              {/* Folhas de apontamento por lote (ADR da Central de Apontamentos). */}
              <Route path="/legado/apontamentos" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["apontamentos_pcp:view"]}><Apontamentos /></ProtectedPage>} />
              <Route path="/legado/mapa" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requireAny={["mapa_producao:view"]}><MapaProducao /></ProtectedPage>} />
              {/* Por ID do setor desde a v3 (o setor virou cadastro); o link antigo por
                  codigo fixo (/setor/corte) e redirecionado pela propria pagina. */}
              <Route path="/legado/mapa/setor/:id" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requireAny={["mapa_producao:view"]}><PaginaSetorMapa /></ProtectedPage>} />
              <Route path="/planejamento-marcenaria" element={<Navigate to="/legado/mapa" replace />} />
              {/* /projetos-executivos/* — submodulo com 17 telas (analises +
                  paineis + base) lendo a planilha de Distribuicao. Entra quem
                  tem o guarda-chuva (projetos_executivos:view) OU qualquer
                  permissao de pagina pe_*:view; a pagina especifica e validada
                  por slug dentro do submodulo (index.jsx). */}
              <Route path="/projetos-executivos/*" element={<ProtectedPage modulo={MODULO_PROJETOS_EXECUTIVOS} requireAny={PE_ROUTE_ANY}><ProjetosExecutivos /></ProtectedPage>} />
              {/* /central-projetos/* — Central de Projetos (ADR-0089). Quem só
                  configura também entra (a tela de configuração confere a dela). */}
              {/* Consentimento do OAuth dos MCPs (Central e ERP, ADR-0091). Sem gate de
                  permissão aqui: o backend mostra só o que a pessoa pode liberar. */}
              <Route path="/oauth/autorizar" element={<ProtectedPage><AutorizarClaude /></ProtectedPage>} />
              {/* Volta do Google ao conectar o Google Agenda (ADR-0093): a tela entrega o código com a sessão. */}
              <Route path="/integracoes/google/retorno" element={<ProtectedPage><RetornoGoogle /></ProtectedPage>} />
              <Route path="/central-projetos/*" element={<ProtectedPage modulo={MODULO_CENTRAL_PROJETOS} requireAny={["central_projetos:view", "central_projetos:configurar"]}><CentralProjetos /></ProtectedPage>} />
              {/* /administracao/importacoes — upload + dry-run + apply + historico.
                  Permissao gate em ProtectedPage; UI tem fallback de "sem permissão". */}
              <Route path="/administracao/importacoes" element={<Navigate to="/configuracoes/importacoes" replace />} />
              {/* /administracao/integracoes-cad — cadastro de credenciais
                  DinaBox API (Bloco E, ADR-0017). Leitura via integracao_cad:view,
                  CRUD via integracao_cad:configurar (+MFA recente). */}
              <Route path="/administracao/integracoes-cad" element={<Navigate to="/configuracoes/integracoes-cad" replace />} />
              {/* /estoque/pendentes virou a fila unica "Materiais a cadastrar" (ADR-0068). */}
              <Route path="/estoque/pendentes" element={<Navigate to="/suprimentos/materiais-a-cadastrar" replace />} />
              {/* /estoque/aproveitamentos (Fase 8.5) virou Aproveitamento de Sobras (D19). */}
              <Route path="/estoque/aproveitamentos" element={<Navigate to="/suprimentos/sobras" replace />} />
              {/* /crm/* — CRM (board de Leads + cockpit). Raiz comercial. */}
              <Route path="/crm/*" element={<ProtectedPage requires={["lead:view"]}><Crm /></ProtectedPage>} />
              {/* /orcamentos/* — Fase 4 turno 4: ficha + editor de ambientes + actions de estado (raiz redireciona p/ /crm) */}
              <Route path="/orcamentos/*" element={<ProtectedPage requires={["orcamento:view"]}><Orcamentos /></ProtectedPage>} />
              {/* /pedidos/* — Fase 4 turno 4: lista + ficha + steps de progresso + revisao */}
              <Route path="/pedidos/*" element={<ProtectedPage requires={["pedido:view"]}><Pedidos /></ProtectedPage>} />
              {/* /comercial/configuracoes — Fase 4 Bloco B: config de status temporal */}
              <Route path="/comercial/configuracoes" element={<ProtectedPage requires={["comercial:view"]}><ConfiguracoesComercial /></ProtectedPage>} />
              {/* /comercial/painel — Fase 4 Bloco D: painel de 8 metricas */}
              <Route path="/comercial/painel" element={<ProtectedPage requires={["comercial:metricas"]}><PainelComercial /></ProtectedPage>} />
              {/* /comercial/formularios/* — construtor de formularios (REF-3). */}
              <Route path="/comercial/formularios/*" element={<ProtectedPage requires={["formulario:view"]}><Formularios /></ProtectedPage>} />
              {/* /comercial/templates — Fase 4 Bloco F: templates de orcamento reutilizaveis */}
              <Route path="/comercial/templates" element={<ProtectedPage requires={["template_orcamento:view"]}><TemplatesPage /></ProtectedPage>} />
              {/* /comercial/parceiros — Bloco F.7: CRUD de Representante (parceiros que recebem RT) */}
              <Route path="/comercial/parceiros" element={<ProtectedPage requires={["representante:view"]}><Parceiros /></ProtectedPage>} />
              {/* /comercial/parceiros/:id — Fase B: cockpit de detalhe do parceiro */}
              <Route path="/comercial/parceiros/:id" element={<ProtectedPage requires={["representante:view"]}><ParceiroDetalhe /></ProtectedPage>} />
              {/* /acompanhamento — Fase 5: painel consolidado pedidos por etapa (planilha + backend) */}
              <Route path="/acompanhamento" element={<ProtectedPage modulo={MODULO_PLANILHA_LEGADO} requires={["acompanhamento:view"]}><Acompanhamento /></ProtectedPage>} />
              {/* /kanbans/* — Fase 6: Kanban configuravel (lista de boards + board drag-drop) */}
              <Route path="/kanbans/*" element={<ProtectedPage requires={["kanban:view"]}><Kanbans /></ProtectedPage>} />
              {/* /suprimentos/solicitacoes/* — Solicitações internas ASM/AST/ASP/RQA/RQI (página do colaborador; mais específica que /suprimentos/*, vence no ranking do router) */}
              <Route path="/suprimentos/solicitacoes/*" element={<ProtectedPage requireAny={PERMS_HUB_SOLICITACOES}><SolicitacoesInternas /></ProtectedPage>} />
              {/* /suprimentos/gestao-solicitacoes — página do gestor (fila de autorização + lotes MDF) */}
              <Route path="/suprimentos/gestao-solicitacoes/*" element={<ProtectedPage requireAny={PERMS_ROTA_GESTAO_SOLICITACOES}><GestaoSolicitacoes /></ProtectedPage>} />
              {/* /suprimentos/painel-assistencias — Fase 5: painel gerencial de assistências (analytics) */}
              <Route path="/suprimentos/painel-assistencias" element={<ProtectedPage requireAny={["solicitacao:painel"]}><PainelAssistencias /></ProtectedPage>} />
              {/* /suprimentos/requisicoes — fila unica do comprador (terceiros + ferragem em falta). Mais específica que /suprimentos/*. */}
              <Route path="/suprimentos/requisicoes" element={<ProtectedPage requires={["compras:view"]}><RequisicoesCompra /></ProtectedPage>} />
              {/* /suprimentos/requisicoes/:id — pagina de detalhe da requisicao (ADR-0069); alvo dos links do sino e do e-mail de mencao. */}
              <Route path="/suprimentos/requisicoes/:id" element={<ProtectedPage requires={["compras:view"]}><RequisicaoDetalhePagina /></ProtectedPage>} />
              {/* /suprimentos/revisor-ferragens — confere a lista do DinaBox e envia à separação (veio do Legado; mais específica que /suprimentos/*) */}
              <Route path="/suprimentos/revisor-ferragens/*" element={<ProtectedPage requires={["revisor_ferragens:view"]}><RevisorFerragens /></ProtectedPage>} />
              {/* /suprimentos/requisicoes-separacao — almoxarife separa ferragens vindas do revisor (mais específica que /suprimentos/*) */}
              <Route path="/suprimentos/requisicoes-separacao" element={<ProtectedPage requires={["separacao_ferragens:view"]}><RequisicoesSeparacao /></ProtectedPage>} />
              {/* /suprimentos/* — Fase 7: Estoque + Compras (router-shell com sub-rotas) */}
              <Route path="/suprimentos/*" element={<ProtectedPage requireAny={["estoque:view", "compras:view"]}><Suprimentos /></ProtectedPage>} />
              {/* /producao/* — Produção v2 (BRIEF DEFINITIVO Fase 4):
                  router-shell único (Painel + detalhes ambiente/pedido/lote +
                  otimizacao/marcenaria/dbr/atrasos/config). As telas antigas
                  /producao/ordens/* e /producao/toc foram REMOVIDAS no P5
                  (decisão de zero coexistência) — URLs antigas caem no
                  catch-all do shell, que redireciona para /producao.
                  /producao/terminal/* segue FORA do shell (AppInner) e vence
                  o catch-all por especificidade. */}
              <Route path="/producao/*" element={<ProtectedPage requireAny={["ambiente_producao:view", "operador:view", "posto_bipagem:view", "ppcp:view", "ppcp:configurar"]}><ProducaoV2Shell /></ProtectedPage>} />
              {/* /terceirizacao/* — Fase 10 (ADR-0025): OSE + Parceiros + Pontualidade */}
              <Route path="/terceirizacao/*" element={<ProtectedPage requires={["terceirizacao:view"]}><TerceirizacaoShell /></ProtectedPage>} />
              {/* /logistica/* — Fase 11 (ADR-0026): Cargas + Entregas + Instalacoes + Cadastros */}
              <Route path="/logistica/*" element={<ProtectedPage requires={["logistica:view"]}><LogisticaShell /></ProtectedPage>} />
              {/* /posvenda/* — Fase 12 (ADR-0027): Assistencia ao Cliente +
                  Satisfacao/NPS (A27). `requireAny` porque as duas telas do
                  modulo tem permissoes distintas: gatear o shell so por
                  `assistencia:view` fazia quem recebeu `satisfacao:view` ver o
                  item no menu e levar "sem permissao" ao clicar. Cada
                  sub-rota se protege por dentro (Posvenda/index.jsx). */}
              <Route path="/posvenda/*" element={<ProtectedPage requireAny={["assistencia:view", "satisfacao:view"]}><PosvendaShell /></ProtectedPage>} />
              {/* /financeiro/* — Fase 13 (ADR-0028 / 0029 / 0030 / 0031):
                  Dashboard + Lancamentos + Cadastros + DRE + Fluxo + Rentabilidade
                  + Custos indiretos + Integracoes bancarias.
                  Single catch-all `/financeiro/*` — FinanceiroShell faz todo o
                  roteamento interno via <Routes>. Coexiste com /financeiro
                  legado (VisaoFinanceira, linha ~259) sem conflito porque o
                  React Router v6 prefere a rota mais especifica (path="/financeiro"
                  exato vence sobre path="/financeiro/*" splat para a URL exata). */}
              <Route path="/financeiro/*" element={<ProtectedPage requires={["financeiro:view"]}><FinanceiroShell /></ProtectedPage>} />
              {/* F14 ADR-0032 - Fiscal NF-e */}
              <Route path="/fiscal/*" element={<ProtectedPage requires={["fiscal:view"]}><FiscalShell /></ProtectedPage>} />
              {/* M5 — Cobranca (Boleto via gateway Asaas) */}
              <Route path="/cobranca/*" element={<ProtectedPage requires={["cobranca:view"]}><CobrancaShell /></ProtectedPage>} />
              {/* ADR-0035 — Marketplace de integracoes plugaveis (read-only, sem permissao especifica) */}
              <Route path="/integracoes" element={<Navigate to="/configuracoes/integracoes" replace />} />
              {/* Agenda v2 (ADR-0090) para quem esta liberada (AGENDA_V2); senao a tela antiga de Minhas Pendencias (REF-16). Pessoal, sem RBAC dedicado. */}
              <Route path="/minhas-pendencias" element={<ProtectedPage requires={[]}><RotaDaAgenda /></ProtectedPage>} />
              {/* ADR-0077 — Chat Pharu (o mesmo chat do PharuCut; a conversa mora aqui) */}
              <Route path="/chat" element={<ProtectedPage requires={["chat:view"]}><ChatPage /></ProtectedPage>} />
              <Route path="/chat/:conversaId" element={<ProtectedPage requires={["chat:view"]}><ChatPage /></ProtectedPage>} />
              {/* M10/M11 — E-mail transacional (ADR-0036) */}
              <Route path="/emails/*" element={<ProtectedPage requires={["email:view"]}><EmailsShell /></ProtectedPage>} />
              {/* ADR-0062 — Webhooks de saida (CRM/Comercial -> ferramentas externas) */}
              <Route path="/webhooks" element={<ProtectedPage requires={["webhook:view"]}><Webhooks /></ProtectedPage>} />
              {/* WhatsApp ADR-0034 — atendimento via whatsapp-web.js, servico Node
                  separado + webhook HMAC + polling no frontend.
                  GATEADO (Fase 9, achado M-TRA-12): enquanto o servico Node nao
                  estiver no ar, a rota nao existe — abrir /whatsapp caia numa tela
                  que nunca carrega era metade do "WhatsApp e fachada". Ligar com
                  REACT_APP_WHATSAPP_ENABLED=true. */}
              {whatsappHabilitado() && (
                <Route path="/whatsapp/*" element={<ProtectedPage requires={["whatsapp:atender"]}><Whatsapp /></ProtectedPage>} />
              )}
              <Route path="*" element={<Navigate to={DEFAULT_PATH} replace />} />
            </Routes>
          </AnimatePresence>
            </PageContent>
          </MainArea>
          <ChatNaLateral />
          </NotificacoesProvider>
          </WhatsappNaoLidasProvider>
          </AgendamentoProvider>
          </ExportRenderProvider>
        </PrecosProvider>
        </PreferenciasTabelasProvider>
      </DadosProvider>
    </ProtectedRoute>
  );
}

/* As rotas quando o navegador está em painel.pharu.com.br.
   Aqui NÃO existe ERP: nem shell autenticado, nem as telas públicas do
   cliente (/p/*, /f/*), nem o terminal de fábrica. Só identidade — porque
   o painel exige login com MFA recente e essas telas são o caminho para
   isso — e o painel em si. Qualquer caminho cai no painel: a navegação
   dele é por estado, não por URL. */
function PainelHostRoutes() {
  // O <title> vive no index.html, que os dois dominios compartilham — sem
  // isto a aba do painel anuncia "PharuERP", o produto vendido ao cliente.
  // Fica aqui porque este componente cobre tambem o /login do painel.
  useEffect(() => {
    document.title = "Painel da Pharu";
  }, []);

  return (
    <>
      {/* O painel precisa deste modal MAIS que o ERP, nao menos: aqui
          TODA leitura exige MFA recente, entao a janela de 15 min expira
          durante o uso normal e cada clique seguinte volta 403. Sem o
          modal montado, o interceptor do AuthContext fica esperando por
          um consumidor que nao existe e a tela congela em silencio — o
          toast desse 403 e suprimido de proposito, porque quem deveria
          explicar e justamente este componente. */}
      <MfaStepUpModal />
      <Routes>
        {/* Login, 2FA e primeiro acesso dividem a mesma moldura (ver TelaDeEntrada). */}
        <Route element={<TelaDeEntrada />}>
          <Route path="/login" element={<Login />} />
          <Route path="/mfa-verify" element={<MfaVerify />} />
          <Route path="/primeiro-acesso" element={<PrimeiroAcesso />} />
        </Route>
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><PainelPharu /></Suspense>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function AppInner() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <>
        <GlobalStyles />
        <PageLoader scope="auth" />
      </>
    );
  }

  /* A decisão mais alta da aplicação: em qual produto este navegador
     está. Feita antes de qualquer rota do ERP existir. */
  if (ehHostDoPainel()) {
    return (
      <>
        <GlobalStyles />
        <PainelHostRoutes />
        <AvisoDeVersaoNova />
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <Routes>
        {/* /login, /mfa-verify e /primeiro-acesso dividem a moldura
            TelaDeEntrada (rota de layout): a cena do fluxo fica montada e so
            o painel troca.
            /mfa-verify — segunda etapa do login para usuarios com MFA ativo
            (Sprint 10). Recebe `mfaToken` via location.state; nao acessivel
            por deep-link direto (redireciona para /login). */}
        <Route element={<TelaDeEntrada />}>
          <Route path="/login" element={<Login />} />
          <Route path="/mfa-verify" element={<MfaVerify />} />
          <Route path="/primeiro-acesso" element={<PrimeiroAcesso />} />
          {/* /convite/:token — publico, fora do shell autenticado; mesma
              moldura do login para quem cria a senha pelo e-mail. */}
          <Route path="/convite/:token" element={<ConviteUsuario />} />
        </Route>
        {/* /p/orcamento/:token — publico (Fase 4 Bloco G), fora do shell, sem chrome.
            Cliente acessa pelo link para aprovar/recusar sem login. */}
        <Route path="/p/orcamento/:token" element={<OrcamentoPublico />} />
        {/* /p/contrato/:token — publico (Bloco F.8), fora do shell.
            Cliente acessa pelo link para assinar contrato com aceite digital. */}
        <Route path="/p/contrato/:token" element={<ContratoPublico />} />
        {/* /p/entrega/:token — publico (Fase 11 ADR-0026), fora do shell.
            Cliente confirma recebimento da entrega digitalmente. */}
        <Route path="/p/entrega/:token" element={<EntregaPublica />} />
        {/* /p/satisfacao/:token — pesquisa de NPS (A27), fora do shell:
            quem responde e o cliente, que nao tem login. */}
        <Route path="/p/satisfacao/:token" element={<SatisfacaoPublica />} />
        {/* /p/proposta/:token — proposta da Pharu para a marcenaria aceitar
            ou recusar sem login (painel v2, área Comercial). */}
        <Route path="/p/proposta/:token" element={<Suspense fallback={<PageLoader />}><PropostaPharuPublica /></Suspense>} />
        {/* /p/cartao-cadastrado — retorno do Stripe depois de o cliente
            cadastrar o cartao da cobranca automatica (ADR-0099). Antes de
            /p/:chave: o segmento fixo vence o parametro. */}
        <Route path="/p/cartao-cadastrado" element={<Suspense fallback={<PageLoader />}><CartaoCadastrado /></Suspense>} />
        {/* /p/:chave — peca de MDF (QR da etiqueta), fora do shell. Um
            segmento so, 30 chars opacos: 8 da unidade + 22 da peca. Nao
            colide com as rotas /p/<coisa>/:token acima, que tem 3 segmentos.
            DEPOIS delas de proposito — o React Router casa por
            especificidade, mas a ordem no arquivo conta a intencao. */}
        <Route path="/p/:chave" element={<PecaPublica />} />
        {/* /f/:slug — formulario publico (REF-3), fora do shell. Visitante
            responde sem login; submissao cria/atualiza lead no CRM. */}
        <Route path="/f/:slug" element={<FormularioPublico />} />
        {/* /producao/terminal/* — Terminal Kiosk (F8.2 / ADR-0023). FORA do
            shell autenticado JWT. Auth propria via PIN + X-Terminal-Token.
            Fullscreen, sem sidebar/topbar. Tablet/celular do chao de fabrica. */}
        <Route path="/producao/terminal/*" element={<TerminalShell />} />
        {/* O painel da Pharu NAO existe neste dominio.
            Nem como pagina, nem como redirecionamento.

            O ERP e um PRODUTO da Pharu. A gestao da empresa que o vende
            nao e uma tela dentro dele — sao publicos diferentes, e o
            painel fala de VARIOS clientes ao mesmo tempo. Manter aqui um
            /painel, ainda que so redirecionando, continuaria dizendo que
            existe uma porta para a plataforma dentro do produto de um
            cliente.

            No dominio do ERP, /painel e um caminho inexistente como
            qualquer outro: cai no shell e vira o destino padrao.

            A UNICA excecao e o localhost, onde nao ha DNS para
            painel.pharu.com.br e sem isso ninguem consegue desenvolver o
            painel. Ver utils/hostDoPainel.js. */}
        {ehAmbienteLocal() && (
          <Route
            path="/painel/*"
            element={
              /* Mesmo embrulho do host de produção: sem ele, quem abre o
                 painel deslogado vê "você não tem acesso" (a resposta do
                 gate) em vez da tela de login, e dev deixa de ensaiar o
                 que produção faz. */
              <>
                {/* Mesmo motivo do host de producao: sem este modal, o 403
                    de "MFA recente" nao tem quem o atenda e o dev nao
                    ensaia o comportamento real — que e justamente onde o
                    defeito nasceu. */}
                <MfaStepUpModal />
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}><PainelPharu /></Suspense>
                </ProtectedRoute>
              </>
            }
          />
        )}
        <Route path="/*" element={<AuthenticatedShell />} />
      </Routes>
      {/* "Nova versão disponível · Atualizar" quando sai deploy novo. */}
      <AvisoDeVersaoNova />
    </>
  );
}

export default function App() {
  return (
    <ThemeContextProvider>
      <ToastProvider>
        <AuthProvider>
          {/* PREF-UI: faz a ponte entre a aparencia (ThemeContext, que envolve
              este provider) e o perfil do usuario no servidor. Precisa estar
              DENTRO do AuthProvider para enxergar o usuario, e nao renderiza
              nada — por isso fica fora do Router. */}
          <SincronizadorPreferencias />
          <BrowserRouter>
            {/* Notificação do chat no aparelho: acerta a inscrição com quem
                está logado e abre a conversa quando a pessoa toca no aviso. */}
            <GuardiaoDasNotificacoes />
            <ErrorBoundary>
              <AppInner />
            </ErrorBoundary>
            <ToastStack />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ThemeContextProvider>
  );
}
